/**
 * OKX V5 frontend-модели для нативных операций.
 *
 * Семантика повторяет BybitNative.model.ts, но с тремя адаптациями:
 *  - posSide ('long'/'short'/'net') вместо positionIdx (число);
 *  - lowercase values для side/orderType (OKX-стандарт V5);
 *  - bar/interval в OKX-формате ('1m', '5m', '1H', '1D').
 */
export interface OkxTickersModel {
    symbol: string
    lastPrice: number
}

export interface OkxOrderIdDto {
    orderId: string
}

export interface OkxPositionDto {
    symbol: string
    leverage: number
    avgPrice: number
    size: number
    liqPrice: number
    unrealisedPnl: number
    posSide: OkxPosSide
    side?: OkxOrderSideTypes
}

export interface OkxOrderDto {
    orderId: string
    time: number
    symbol: string
    price: number
    size: number
    side: OkxOrderSideTypes
    type: OkxOrderTypes
    status: OkxOrderStatusType
}

export interface OkxCancelOrderDto {
    symbol: string
    orderId: string
}

export interface OkxBalanceDto {
    balance: number
}

export interface OkxLimitDto {
    maxLimit: number
    maxLeverage: number
}

export interface OkxChangeLeverageDto {
    symbol: string
    leverage: number
    posSide?: OkxPosSide
}

export interface OkxGetOrdersHistoryDto {
    symbol: string
    // Период (мс, начало) и лимит — для выбора периода в trade history.
    startTime?: number
    limit?: number
}

export interface OkxOrderHistoryDto {
    orderId: string
    time: number
    symbol: string
    price: number
    size: number
    side: OkxOrderSideTypes
    commission: number
    pnl: number
}

export interface OkxGetPositionsHistoryDto {
    symbol: string
}

export interface OkxPositionHistoryDto {
    orderId: string
    time: number
    symbol: string
    size: number
    side: OkxOrderSideTypes
    pnl: number
    entryPrice: number
    exitPrice: number
}

export interface OkxGetInstrumentInfoDto {
    symbol: string
    priceStep: number
    quantityStep: number
    /** OKX-специфика: размер 1 контракта в базовой валюте */
    ctVal: number
}

/**
 * OKX режим позиций — на уровне аккаунта (не per-symbol как Bybit).
 *   long_short_mode — hedge (long+short одновременно)
 *   net_mode        — one-way (одна позиция)
 */
export enum OkxPosMode {
    LongShort = 'long_short_mode',
    Net = 'net_mode',
}

/**
 * Сторона позиции в ордере.  В net_mode используется 'net' или поле
 * вообще опускается; в long_short_mode обязательно 'long' или 'short'.
 */
export enum OkxPosSide {
    Long = 'long',
    Short = 'short',
    Net = 'net',
}

export interface OkxSwitchPositionModeDto {
    posMode: OkxPosMode
}

export interface OkxGetPositionDto {
    symbol: string
}

export interface OkxRiskLimitsDto {
    limits: OkxLimitDto[]
}

export enum OkxCandlesIntervalTypes {
    min1 = '1m',
    min3 = '3m',
    min5 = '5m',
    min15 = '15m',
    min30 = '30m',
    hour1 = '1H',
    hour2 = '2H',
    hour4 = '4H',
    hour6 = '6H',
    hour12 = '12H',
    day1 = '1D',
    week1 = '1W',
    mon1 = '1M',
}

/** OKX V5 использует lowercase side ('buy'/'sell'), не 'Buy'/'Sell' как Bybit. */
export enum OkxOrderSideTypes {
    Buy = 'buy',
    Sell = 'sell',
}

/** OKX V5 ordType — lowercase. */
export enum OkxOrderTypes {
    Limit = 'limit',
    Market = 'market',
    PostOnly = 'post_only',
    FOK = 'fok',
    IOC = 'ioc',
}

export enum OkxOrderStatusType {
    Live = 'live',
    PartiallyFilled = 'partially_filled',
    Filled = 'filled',
    Cancelled = 'canceled',
    MmpCancelled = 'mmp_canceled',
}

export interface OkxSendOrderDto {
    symbol: string
    side: OkxOrderSideTypes
    orderType: OkxOrderTypes
    posSide?: OkxPosSide
    size: number
    price?: number
}
